import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'

import { login } from '../../redux/actions/authAction'
import { getErrorMessage } from '../../utils'

const FEATURES = [
    { icon: 'fa-magic', text: 'Scan invoices, bills and price lists into items automatically' },
    { icon: 'fa-cubes', text: 'Keep every item with its vendor, date and contact details' },
    { icon: 'fa-line-chart', text: 'Compare prices across vendors to find the best deal' },
]

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const Login = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { user, checked } = useSelector(state => state.auth)

    const [form, setForm] = useState({ email: '', password: '', remember: false })
    const [showPassword, setShowPassword] = useState(false)
    const [errors, setErrors] = useState({})
    const [serverError, setServerError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    if (checked && user) return <Navigate to="/dashboard" replace />

    const handleChange = (e) => {
        const { name, value, type, checked: isChecked } = e.target
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? isChecked : value }))
        setErrors(prev => ({ ...prev, [name]: '' }))
        setServerError('')
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const errs = {}
        if (!form.email.trim()) errs.email = 'Enter your email address'
        else if (!EMAIL.test(form.email.trim())) errs.email = 'Enter a valid email address'
        if (!form.password) errs.password = 'Enter your password'
        setErrors(errs)
        if (Object.keys(errs).length) return

        setSubmitting(true)
        try {
            await dispatch(login({ email: form.email.trim(), password: form.password, remember: form.remember }))
            navigate('/dashboard', { replace: true })
        } catch (err) {
            setServerError(getErrorMessage(err, 'Could not sign in'))
            setSubmitting(false)
        }
    }

    return (
        <div className="LoginPage">
            <aside className="LoginBrand">
                <div className="LoginLogo">
                    <i className="fa fa-file-text-o" aria-hidden="true"></i>
                    <span>DocScanner <b>CRM</b></span>
                </div>
                <div className="LoginPitch">
                    <h1>Every invoice, every item, in one place.</h1>
                    <ul>
                        {FEATURES.map(f => (
                            <li key={f.icon}>
                                <span><i className={`fa ${f.icon}`} aria-hidden="true"></i></span>
                                {f.text}
                            </li>
                        ))}
                    </ul>
                </div>
                <p className="LoginFoot">© {new Date().getFullYear()} DocScanner CRM</p>
            </aside>

            <main className="LoginMain">
                <form className="LoginCard" onSubmit={handleSubmit} noValidate>
                    <div className="LoginLogo LoginLogoMobile">
                        <i className="fa fa-file-text-o" aria-hidden="true"></i>
                        <span>DocScanner <b>CRM</b></span>
                    </div>
                    <h2>Welcome back</h2>
                    <p className="LoginSub">Sign in to continue to your dashboard</p>

                    {serverError && (
                        <div className="LoginAlert" role="alert">
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i> {serverError}
                        </div>
                    )}

                    <div className="LoginField">
                        <label htmlFor="email">Email address</label>
                        <div className={`LoginInput ${errors.email ? 'Invalid' : ''}`}>
                            <i className="fa fa-envelope-o" aria-hidden="true"></i>
                            <input id="email" name="email" type="email" autoComplete="username" autoFocus
                                placeholder="you@company.com" value={form.email} onChange={handleChange} />
                        </div>
                        {errors.email && <small>{errors.email}</small>}
                    </div>

                    <div className="LoginField">
                        <label htmlFor="password">Password</label>
                        <div className={`LoginInput ${errors.password ? 'Invalid' : ''}`}>
                            <i className="fa fa-lock" aria-hidden="true"></i>
                            <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                                placeholder="Enter your password" value={form.password} onChange={handleChange} />
                            <button type="button" className="TogglePassword" onClick={() => setShowPassword(v => !v)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
                            </button>
                        </div>
                        {errors.password && <small>{errors.password}</small>}
                    </div>

                    <label className="LoginRemember">
                        <input type="checkbox" name="remember" checked={form.remember} onChange={handleChange} />
                        Keep me signed in for 7 days
                    </label>

                    <button type="submit" className="LoginButton" disabled={submitting}>
                        {submitting ? <><i className="fa fa-spinner fa-spin"></i> Signing in...</> : 'Sign in'}
                    </button>
                </form>
            </main>
        </div>
    )
}

export default Login
